/**
 * React hooks for accessing WebMCP from components.
 */

import { useEffect } from "react"
import type { CogoToolRegistry, WebMCPTool } from "../core/tool-registry"
import type { WebMCPStateProvider } from "../core/state-provider"
import { useWebMCPContext } from "./webmcp-provider"

/**
 * Access the CogoToolRegistry from context.
 * Must be used within a <WebMCPProvider>.
 */
export function useWebMCPRegistry(): CogoToolRegistry {
  const { registry } = useWebMCPContext()
  return registry
}

/**
 * Access the WebMCPStateProvider from context.
 * Returns null if no state provider has been set (runtime not yet ready).
 */
export function useWebMCPState(): WebMCPStateProvider | null {
  const { stateProvider } = useWebMCPContext()
  return stateProvider
}

/**
 * Register a single tool at component mount, unregister on unmount.
 * Useful for component-level tool exposure.
 */
export function useWebMCPTool(toolDef: WebMCPTool): void {
  const { registry } = useWebMCPContext()

  useEffect(() => {
    registry.register(toolDef)
    return () => {
      registry.unregister(toolDef.name)
    }
  }, [registry, toolDef.name]) // eslint-disable-line react-hooks/exhaustive-deps
}
